<script>
import { BadgeCheck, Crown, ShieldCheck, Layers3, Footprints, Crosshair, ChevronRight } from '@lucide/vue';
import ExperienceSlider from './ExperienceSlider.vue';
import { checkpoints, clamp, experienceAt, portraitBlend, powerCheckpoint, settleCheckpoint } from './ExperienceControl.js';

export default {
    name: 'ExperienceControl',
    props: {
        modelValue: { type: Number, default: 0 }, // 0 Standard, 1 Deluxe, 2 Ultra
    },
    emits: ['update:modelValue'],
    components: { ExperienceSlider, BadgeCheck, Crown, ShieldCheck, Layers3, Footprints, Crosshair, ChevronRight },
    data() {
        const start = checkpoints[this.modelValue]?.value ?? 0;
        return {
            target: start,
            progress: start,
            dragging: false,
            playing: false,
            reduced: false,
            visible: true,
            loaded: false,
            committed: start,
            frame: 0,
            previousTime: null,
            playbackFrom: 0,
            playbackStart: 0,
            disposed: false,
            motionQuery: null,
            checkpoints,
            powerCheckpoint,
            stages: ['frame-1.webp', 'frame-2.webp', 'frame-3.webp', 'premium-room.png', 'ultimate-room.webp'].map(file => ({ photo: `https://dr4y4dqumhrhn.cloudfront.net/${file}` })),
        };
    },
    computed: {
        experience() { return experienceAt(this.progress); },
        targetExperience() { return experienceAt(this.target); },
        tier() { return this.experience.tier; },
        hoursLabel() { return this.experience.label; },
        power() { return this.progress / 100; },
        roomReveal() { return this.ease((this.progress - powerCheckpoint) / (100 - powerCheckpoint)); },
        finalPower() { return this.roomReveal; },
        slippersPower() { return this.tier >= 1 ? 1 : 0; },
        sliderValue() { return this.dragging ? this.target : this.progress; },
        blend() { return portraitBlend(this.progress); },
        poseOpacities() { return this.blend.poses; },
        groupReveal() { return this.blend.opening; },
    },
    watch: {
        target() { this.startAnimation(); },
        dragging() { this.startAnimation(); },
        reduced() { this.startAnimation(); },
        committed(value) {
        const tier = experienceAt(value).tier;
            if (tier !== this.modelValue) this.$emit('update:modelValue', tier);
        },
        modelValue(tier) {
            const value = checkpoints[tier]?.value;
            if (value !== undefined && value !== this.committed) this.commit(value);
        },
    },
    mounted() {
        this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
        this.updateMotion();
        this.motionQuery.addEventListener('change', this.updateMotion);
        document.addEventListener('visibilitychange', this.updateVisibility);
        const images = [...this.stages.map(stage => stage.photo), 'https://dr4y4dqumhrhn.cloudfront.net/frame-0.webp', 'https://dr4y4dqumhrhn.cloudfront.net/premium-therapist.png', 'https://dr4y4dqumhrhn.cloudfront.net/ultimate-therapist-refined.png'];
        Promise.all(images.map(src => new Promise(resolve => {
            const img = new Image();
            img.onload = () => img.decode().catch(() => {}).then(resolve);
            img.onerror = resolve;
            img.src = src;
        }))).then(() => { if (!this.disposed) this.loaded = true; });
    },
    beforeUnmount() {
        this.disposed = true;
        cancelAnimationFrame(this.frame);
        this.motionQuery?.removeEventListener('change', this.updateMotion);
        document.removeEventListener('visibilitychange', this.updateVisibility);
    },
    methods: {
        ease(value) { const n = clamp(value, 0, 1); return n * n * (3 - 2 * n); },
        updateMotion() { this.reduced = this.motionQuery.matches; },
        updateVisibility() {
            this.visible = !document.hidden;
            if (!this.visible) { this.playing = false; cancelAnimationFrame(this.frame); this.frame = 0; this.previousTime = null; }
            else this.startAnimation();
        },
        startAnimation() {
            if (!this.frame && this.visible && !this.disposed) this.frame = requestAnimationFrame(this.animate);
        },
        animate(now) {
            this.frame = 0;
            const delta = Math.min(40, now - (this.previousTime ?? now - 16));
            this.previousTime = now;
            if (this.playing) {
                this.target = Math.min(100, this.playbackFrom + (now - this.playbackStart) / 105);
                if (this.target === 100) { this.playing = false; this.committed = 100; }
            }
            const next = this.reduced ? this.target : this.progress + (this.target - this.progress) * (1 - Math.exp(-delta / (this.dragging ? 45 : 95)));
            this.progress = Math.abs(next - this.target) < .08 ? this.target : next;
            if (this.playing || this.progress !== this.target) this.startAnimation();
            else this.previousTime = null;
        },
        choose(value) { this.playing = false; this.target = clamp(value); },
        commit(value) { this.committed = value; this.dragging = false; this.choose(value); },
        release(value) { this.commit(settleCheckpoint(value, this.committed)); },
        cancelDrag() { this.dragging = false; this.choose(this.committed); },
        onSliderChange(value) { this.dragging = true; this.choose(value); },
        navigateCheckpoint(key) {
            const index = checkpoints.findIndex(point => point.value === this.committed);
            const next = key === 'Home' ? 0 : key === 'End' ? 2 : clamp(index + (['ArrowRight', 'ArrowUp', 'PageUp'].includes(key) ? 1 : -1), 0, 2);
            this.commit(checkpoints[next].value);
        },
        surgeStyle(i) { return { '--surge-x': `${8 + (i * 19) % 88}%`, '--surge-delay': `${-i * .19}s`, '--surge-speed': `${1.15 + (i % 4) * .22}s` }; },
        sparkStyle(i) { return { '--spark-x': `${6 + (i * 23) % 91}%`, '--spark-y': `${18 + (i * 17) % 68}%`, '--spark-delay': `${-i * .23}s`, '--spark-duration': `${1.6 + (i % 5) * .2}s` }; },
    },
};
</script>

<template>
    <div class="mockup-page" :data-visible="visible">
        <div class="mockup-shell">
            <div class="experience-display" :style="{ '--room-reveal': roomReveal }">
                <section class="experience-scene" :data-stage="tier" :data-loaded="loaded" :style="{ '--charge': power, '--ultimate': finalPower, '--room-reveal': roomReveal, '--opening': groupReveal, '--palette-shift': ease((progress - 65) / 13), '--upgrade-surge': Math.sin(Math.PI * roomReveal) }" aria-label="Therapist transformation">
                    <div class="mobile-artwork-backdrop" aria-hidden="true" :style="{ opacity: poseOpacities[3] }" />
                    <div class="v3-artwork" aria-hidden="true">
                        <img v-for="(stage,index) in stages" :key="stage.photo" :class="index < 3 ? 'standard-artwork' : index === 3 ? 'room-artwork premium-room' : 'room-artwork'" :src="stage.photo" alt="" draggable="false" :style="{opacity:poseOpacities[index]}" />
                        <img class="standard-artwork" src="https://dr4y4dqumhrhn.cloudfront.net/frame-0.webp" alt="" draggable="false" :style="{ opacity: groupReveal }" />
                        <div class="premium-yellow-shade" :style="{ opacity: poseOpacities[3] }" />
                        <div class="reading-shade" :style="{ opacity: roomReveal }" />
                        <div class="glass-benefit-panel" aria-hidden="true" />
                        <div class="ultimate-energy" :style="{ opacity: roomReveal }">
                            <div class="upgrade-sweep" />
                            <div v-if="tier === 2" class="upgrade-burst" />
                            <div class="power-lettering"><span>POWER</span></div>
                            <div class="surge-lines"><i v-for="i in 16" :key="i" :style="surgeStyle(i - 1)" /></div>
                        </div>
                        <img class="premium-portrait" src="https://dr4y4dqumhrhn.cloudfront.net/premium-therapist.png" alt="" draggable="false" :style="{ opacity: poseOpacities[3] }" />
                        <img class="ultimate-portrait" src="https://dr4y4dqumhrhn.cloudfront.net/ultimate-therapist-refined.png" alt="" draggable="false" :style="{ opacity: roomReveal }" />
                    </div>
                    <div v-if="tier > 0" class="tier-corner"><div class="tier-corner-label"><span>TOP</span><b>{{ tier === 2 ? '10%' : '20%' }}</b></div></div>
                    <div class="epic-light" aria-hidden="true" />
                    <div class="aura-particles" aria-hidden="true"><i v-for="i in 14" :key="i" :style="{'--i':i - 1}" /></div>
                    <div class="scene-content">
                        <div class="benefit experience-benefit"><span class="gold-icon"><BadgeCheck /></span><div><span class="benefit-label">Therapist Experience</span><div class="hours-line" :data-range="progress === 0"><strong class="gold-lettering">{{ hoursLabel }}</strong><span>hr served</span></div></div></div>
                        <div class="benefits">
                            <div class="benefit final-benefit" :style="{ '--reveal': finalPower }" :aria-hidden="finalPower < .05"><span class="gold-icon"><Crown /></span><div><strong class="gold-lettering">VIP ROOM</strong></div></div>
                            <div class="benefit money-back-benefit" :style="{ '--reveal': slippersPower }" :aria-hidden="tier < 1"><span class="gold-icon"><ShieldCheck /></span><div><span class="benefit-label">15mins MONEY BACK</span><strong class="money-back-word gold-lettering">GUARANTEED</strong></div></div>
                            <div class="benefit booster-benefit" :style="{ '--reveal': slippersPower }" :aria-hidden="tier < 1"><template v-if="tier > 0"><span class="gold-icon"><Crosshair /></span><div><strong class="gold-lettering">Customize</strong><span class="benefit-label">Wellness Booster</span></div></template></div>
                            <div class="benefit towel-benefit" :style="{ '--reveal': slippersPower }" :aria-hidden="tier < 1"><span class="gold-icon"><Layers3 /></span><div><span class="benefit-label">Disposable</span><strong class="gold-lettering">TOWEL <small>×</small>{{ tier === 2 ? 3 : 1 }}</strong></div></div>
                            <div class="benefit slipper-benefit" :style="{ '--reveal': slippersPower }" :aria-hidden="tier < 1"><span class="gold-icon"><Footprints /></span><div><span class="benefit-label">Disposable</span><strong class="gold-lettering">SLIPPER <small>×</small>1</strong></div></div>
                        </div>
                    </div>
                    <!-- <div class="bank-promo" :data-visible="tier > 0" :aria-hidden="tier === 0">
                        <img src="https://dr4y4dqumhrhn.cloudfront.net/public-bank-logo.png" alt="Public Bank" draggable="false" />
                        <span>Special Promo</span>
                    </div> -->
                </section>

                <section class="experience-bar" :data-tier="tier" :data-idle="target === 0 && !dragging && progress < .1" :data-dragging="dragging" :data-powered="dragging || target >= powerCheckpoint || progress >= powerCheckpoint" :data-turbo="sliderValue === 100" :style="{ '--fill': `${sliderValue}%`, '--standard-offset': `${-25 * (1 - Math.min(sliderValue / powerCheckpoint, 1))}px` }">
                    <div class="bar-sparks" aria-hidden="true"><i v-for="i in (sliderValue === 100 ? 48 : 16)" :key="i" :style="sparkStyle(i - 1)" /></div>
                    <h2 id="experience-label" class="dynamic-experience-title" aria-live="polite">
                        <span class="bar-tier-name" :data-tier="tier"><span>{{ tier === 0 ? 'Standard' : tier === 1 ? 'DELUXE' : 'ULTRA' }}</span></span>
                        <span>Experience</span>
                    </h2>
                    <div class="rail-area">
                        <ExperienceSlider :value="sliderValue" :value-text="`${targetExperience.label} hours served`" @change="onSliderChange" @commit="release" @cancel="cancelDrag" @navigate="navigateCheckpoint" />
                        <span v-if="!dragging && tier > 0 && Math.abs(progress - target) < .1" class="tier-tagline" :key="tier" :style="{ '--tagline-progress': sliderValue / 100 }">{{ tier === 2 ? 'The perfect blend of privacy and mastery.' : 'The upgraded break you deserve.' }}</span>
                        <span v-if="target === 0 && !dragging" class="swipe-hint">
                            <span class="swipe-arrows" aria-hidden="true"><ChevronRight /><ChevronRight /></span>
                            <!-- <span class="swipe-copy">Swipe for Public Bank<br />Special Offer</span> -->
                        </span>
                        <div class="stage-stops"><button v-for="checkpoint in checkpoints" :key="checkpoint.value" :data-checkpoint="checkpoint.value" :style="{left:`${checkpoint.value}%`}" :aria-label="`${checkpoint.label} hours checkpoint`" :aria-pressed="target === checkpoint.value" @pointerdown.stop @click="commit(checkpoint.value)"><i aria-hidden="true" /></button></div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* User-pinned mockup: gender row, lightning Experience slider, one transforming panel, View Time.
    Gold-on-black icons and type, five supplied portrait stages; final aura responds to scrub progress. */

* {
    box-sizing: border-box
}

button,
input,
select {
    font: inherit
}

button,
a,
select {
    -webkit-tap-highlight-color: transparent
}

button {
    cursor: pointer;
    color: inherit
}

button:disabled {
    opacity: .35;
    cursor: default
}

button:focus-visible,
a:focus-visible,
select:focus-visible {
    outline: 3px solid #fff2bd;
    outline-offset: 4px
}

h2,
p {
    margin: 0
}

.mockup-shell {
    max-width: 830px;
    margin: auto
}

.experience-bar {
    background: linear-gradient(135deg, #272014, #100f0b 55%, #292012);
    border: 3px solid #c3913f;
    border-radius: 25px;
    padding: 16px 25px 14px;
    margin-bottom: 18px;
    box-shadow: inset 0 1px 0 #ffe29b7a, 0 5px 12px #8a4e281c;
    position: relative
}

.energy-status {
    font-size: 11px;
    letter-spacing: .07em;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #d5b275;
    white-space: nowrap
}

.rail-area {
    position: relative;
    padding-bottom: 25px
}

.rail-energy {
    position: absolute;
    top: 14px;
    left: 19px;
    right: 19px;
    height: 13px;
    border: 1px solid #7d6032;
    background: #14120d;
    border-radius: 8px;
    overflow: hidden;
    pointer-events: none
}

.energy-trail,
.energy-core {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: 8px
}

.energy-trail {
    width: var(--trail);
    background: #a37526;
    box-shadow: 0 0 13px #ffc95288
}

.energy-core {
    width: var(--fill);
    background: linear-gradient(90deg, #95651e, #e4b348 65%, #ffe9a7);
    box-shadow: 0 0 14px #ffd469aa;
    overflow: hidden
}

.energy-core:after {
    content: '';
    position: absolute;
    inset: -10px -80px;
    background: linear-gradient(100deg, transparent 30%, #ffefb3b0 47%, transparent 64%);
    transform: translateX(-100%);
    animation: bar-stream 2.2s linear infinite
}

.energy-texture {
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(90deg, transparent 0, transparent calc(3.333% - 2px), #0e0b07b0 calc(3.333% - 2px), #0e0b07b0 3.333%);
    opacity: .6
}

.experience-slider {
    height: 42px;
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center
}

.thunder-follow {
    position: absolute;
    top: 9px;
    transform: translateX(-50%);
    width: 24px;
    height: 24px;
    z-index: 3;
    pointer-events: none;
    color: #62410f;
    display: grid;
    place-items: center
}

.thunder-follow svg {
    width: 21px;
    height: 21px;
    stroke-width: 2
}

.experience-bar[data-powered=true] .thunder-follow svg {
    filter: drop-shadow(0 0 3px #fff1b2);
    animation: thunder-pulse .9s ease-in-out infinite alternate
}

.stage-stops {
    position: absolute;
    bottom: 0;
    left: 19px;
    right: 19px;
    display: flex;
    justify-content: space-between;
    pointer-events: none
}

.stage-stops button {
    position: relative;
    border: 0;
    padding: 4px 0 0;
    background: transparent;
    width: 1px;
    display: flex;
    align-items: center;
    flex-direction: column;
    color: #ac9468;
    pointer-events: auto;
    overflow: visible;
    white-space: nowrap
}

.stage-stops button:before {
    content: '';
    position: absolute;
    inset: -10px -22px
}

.stage-stops i {
    height: 4px;
    width: 2px;
    background: #a88b4f;
    margin-bottom: 4px
}

.stage-stops span {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    transition: color .2s
}

.stage-stops button[aria-pressed=true] {
    color: #ffe8a0
}

.stage-stops button[aria-pressed=true] i {
    background: #ffe8a0;
    box-shadow: 0 0 6px #eab852
}

.experience-scene {
    isolation: isolate;
    position: relative;
    overflow: hidden;
    border: 5px solid #b68332;
    border-radius: 33px;
    background: #151109;
    min-height: 556px;
    aspect-ratio: 1.49;
    box-shadow: inset 0 0 0 1px #f2c76960, 0 8px 20px #7f48222a
}

.photo-stage {
    position: absolute;
    z-index: -2;
    inset: 0 0 0 41%;
    overflow: hidden;
    background: #17120b
}

.stage-photo {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    user-select: none;
    pointer-events: none
}

.photo-blend {
    position: absolute;
    z-index: 10;
    inset: 0;
    background: linear-gradient(90deg, #151109 0%, #151109a0 12%, transparent 38%), linear-gradient(0deg, #100c07a8, transparent 24%)
}

.scene-content {
    position: relative;
    width: 60%;
    padding: 20px 0 99px 19px
}

.benefit {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 75px;
    position: relative
}

.gold-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 65px;
    height: 65px;
    border-radius: 50%;
    border: 2px solid #c99b4e;
    color: #f3cd75;
    background: radial-gradient(circle at 40% 20%, #392c16, #0e0d0a 75%);
    box-shadow: inset 0 0 0 4px #17130e, inset 0 0 0 5px #75592e66
}

.gold-icon svg {
    width: 38px;
    height: 38px;
    stroke-width: 1.7
}

.benefit-label {
    display: block;
    font-size: 23px;
    line-height: 1.15;
    color: #dfc18b
}

.hours-line {
    display: flex;
    align-items: baseline;
    gap: 6px;
    white-space: nowrap
}

.hours-line>strong {
    font-size: 40px;
    line-height: 1.1;
    letter-spacing: -.025em
}

.hours-line>span {
    font-size: 20px;
    color: #e2c68f
}

.gold-lettering {
    color: #f6d280;
    background-image: linear-gradient(110deg, #bc8936 0%, #f9d88d 24%, #f8d893 43%, #fff8dc 47%, #f8d893 52%, #c8943b 73%, #fae2a9 100%);
    background-size: 250% 100%;
    background-position: 100% center;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 2px 13px #eec46613
}

.benefits {
    position: relative;
    --row-height: 75px;
    --row-gap: 6px
}

.benefits .benefit {
    margin-top: var(--row-gap);
    height: var(--row-height);
    min-height: var(--row-height)
}

.benefits strong {
    display: inline-block;
    font-size: 35px;
    line-height: 1.03;
    letter-spacing: -.025em;
    font-weight: 800;
    white-space: nowrap
}

.benefits small {
    font-size: 21px;
    margin-left: 2px;
    font-weight: 400
}

.benefit-secondary {
    font-size: 18px;
    color: #dbc28d;
    display: inline-block;
    margin-left: 6px
}

.final-benefit,
.slipper-benefit {
    opacity: var(--reveal);
    transform: translateY(calc(12px * (1 - var(--reveal))));
    pointer-events: none
}

.slipper-benefit {
    transition: none;
    transform: translateY(calc(-3 * (var(--row-height) + var(--row-gap)) * (1 - var(--ultimate))))
}

.treatment-copy {
    position: absolute;
    bottom: 19px;
    left: 21px;
    z-index: 15;
    font-size: 22px;
    line-height: 1.23;
    max-width: 70%;
    color: #dfc18b
}

.treatment-copy strong {
    display: block;
    font-size: 23px;
    margin-top: 5px;
    color: #edcf8b
}

.epic-light {
    position: absolute;
    z-index: -1;
    inset: 0;
    background: radial-gradient(ellipse at 92% 30%, #ffbf442f, transparent 62%);
    mix-blend-mode: screen;
    opacity: var(--ultimate);
    pointer-events: none
}

.aura-particles {
    position: absolute;
    z-index: 12;
    inset: 0 0 0 50%;
    opacity: var(--ultimate);
    pointer-events: none;
    overflow: hidden
}

.aura-particles i {
    position: absolute;
    left: calc(4% + var(--i)*7%);
    bottom: -12px;
    width: 3px;
    height: 7px;
    border-radius: 50%;
    background: #ffeab2;
    box-shadow: 0 0 7px #ffb830;
    animation: embers calc(3.1s + var(--i)*.13s) calc(var(--i)*-.41s) linear infinite
}

.experience-scene[data-stage='4'] .gold-lettering {
    animation: gold-shimmer 4.5s ease-in-out infinite
}

.experience-scene[data-stage='4'] .gold-icon {
    animation: icon-glow 4.5s ease-in-out infinite
}

@keyframes gold-shimmer {

    0%,
    15% {
        background-position: 100% center
    }

    65%,
    100% {
        background-position: -100% center
    }
}

@keyframes bar-stream {
    to {
        transform: translateX(100%)
    }
}

@keyframes thunder-pulse {
    to {
        transform: scale(.87);
        filter: drop-shadow(0 0 6px #fff3c2)
    }
}

@keyframes embers {
    0% {
        transform: translate(0, 0) scale(.5);
        opacity: 0
    }

    15% {
        opacity: .8
    }

    85% {
        opacity: .65
    }

    100% {
        transform: translate(-22px, -560px) scale(1.1);
        opacity: 0
    }
}

@keyframes icon-glow {

    0%,
    100% {
        filter: drop-shadow(0 0 0 #f7c75e00)
    }

    50% {
        filter: drop-shadow(0 0 5px #f7c75e48)
    }
}

.mockup-page[data-visible=false] * {
    animation-play-state: paused !important
}

@media(prefers-reduced-motion:reduce) {

    *,
    *:before,
    *:after {
        animation: none !important;
        transition: none !important;
        scroll-behavior: auto !important
    }
}

/* Targeted correction: three checkpoints, warm controls, plain normal range, phone-first type. */
.mockup-shell {
    max-width: 600px
}

.experience-bar {
    background: #ee9e3e;
    border-color: #b57927;
    box-shadow: inset 0 0 0 5px #fac54f, inset 0 0 0 8px #b57927;
    padding: 18px 25px 17px
}

.energy-status {
    color: #754719;
    font-size: 12px;
    letter-spacing: 0
}

.rail-energy {
    background: #f4b847;
    border-color: #aa701f
}

.energy-core {
    background: linear-gradient(90deg, #f17e28, #ffad3d 65%, #ffe496)
}

.energy-trail {
    background: #dd9530
}

.energy-texture {
    background: repeating-linear-gradient(90deg, transparent 0, transparent calc(3.333% - 2px), #b877233d calc(3.333% - 2px), #b877233d 3.333%)
}

.stage-stops {
    left: 19px;
    right: 19px
}

.stage-stops span {
    font-size: 13px;
    font-weight: 700
}

.stage-stops button,
.stage-stops button[aria-pressed=true] {
    color: #704217
}

.stage-stops i {
    background: #ad7026
}

.stage-stops button[aria-pressed=true] i {
    background: #704217;
    box-shadow: none
}

.stage-stops button:first-child span {
    transform: translateX(24%)
}

.stage-stops button:last-child span {
    transform: translateX(-16%)
}

.experience-scene {
    min-height: 440px;
    aspect-ratio: 1.3;
    background: #ffca55;
    transition: background .35s, color .35s;
    border-radius: 25px
}

.photo-stage {
    left: 40%;
    background: #ffca55;
    z-index: -2
}

.photo-blend {
    background: linear-gradient(90deg, #ffca55, transparent 25%);
    transition: background .35s
}

.vip-scene {
    position: absolute;
    z-index: -3;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: right center;
    pointer-events: none
}

.scene-content {
    width: 66%;
    padding: 18px 0 95px 14px
}

.benefit {
    gap: 8px;
    min-height: 60px
}

.gold-icon {
    width: 45px;
    height: 45px
}

.gold-icon svg {
    width: 28px;
    height: 28px
}

.benefit-label {
    font-size: 17px
}

.hours-line>strong {
    font-size: 35px;
    font-variant-numeric: tabular-nums;
    min-width: 3ch
}

.hours-line>span {
    font-size: 15px
}

.benefits {
    --row-height: 60px;
    --row-gap: 5px
}

.benefits strong {
    font-size: 26px;
    letter-spacing: -.015em
}

.benefits small {
    font-size: 17px
}

.benefit-secondary {
    font-size: 14px
}

.treatment-copy {
    font-size: 16px;
    max-width: 92%;
    left: 15px;
    bottom: 17px
}

.treatment-copy strong {
    font-size: 17px;
    line-height: 1.3
}

.experience-scene[data-stage='0'] .gold-lettering {
    color: #845321;
    background: none;
    -webkit-text-fill-color: currentColor;
    text-shadow: none;
    animation: none;
    font-weight: 700
}

.experience-scene[data-stage='0'] .benefit-label,
.experience-scene[data-stage='0'] .hours-line>span,
.experience-scene[data-stage='0'] .treatment-copy,
.experience-scene[data-stage='0'] .treatment-copy strong {
    color: #78501e
}

.experience-scene[data-stage='0'] .gold-icon {
    color: #875620;
    background: #ffd77a;
    border-color: #b57927;
    box-shadow: none;
    animation: none
}

.experience-scene[data-stage='1'] {
    background: #a9702d;
    border-color: #966020
}

.experience-scene[data-stage='1'] .photo-blend {
    background: linear-gradient(90deg, #a9702d, transparent 32%)
}

.experience-scene[data-stage='1'] .gold-lettering {
    color: #fff1bd;
    background: none;
    -webkit-text-fill-color: currentColor;
    font-weight: 800;
    text-shadow: none
}

.experience-scene[data-stage='1'] .benefit-label,
.experience-scene[data-stage='1'] .hours-line>span,
.experience-scene[data-stage='1'] .treatment-copy,
.experience-scene[data-stage='1'] .treatment-copy strong {
    color: #fff0c5
}

.experience-scene[data-stage='1'] .gold-icon {
    background: #956127;
    color: #ffdc89;
    border-color: #edbf68;
    box-shadow: none
}

.experience-scene[data-stage='2'] {
    background: #20180c
}

.experience-scene[data-stage='2'] .gold-lettering {
    animation: gold-shimmer 4.5s ease-in-out infinite
}

.experience-scene[data-stage='2'] .gold-icon {
    animation: icon-glow 4.5s ease-in-out infinite
}

.experience-scene[data-stage='2'] .scene-content {
    background: linear-gradient(90deg, #160e069c, transparent)
}

.experience-scene[data-stage='2'] .treatment-copy {
    text-shadow: 0 1px 5px #120b04, 0 1px 10px #120b04
}

.final-benefit,
.slipper-benefit {
    transition: opacity .25s, transform .3s
}

/* Three-stop control; the last two destinations deliberately sit close together. */
.experience-bar {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 12px;
    padding: 9px 15px 8px;
    border-radius: 23px;
    background: #fac54f;
    box-shadow: none;
    min-height: 78px
}

.experience-bar h2 {
    font-size: 18px;
    font-weight: 800;
    color: #795020;
    line-height: 1.2
}

.rail-area {
    height: 52px;
    padding: 0;
    min-width: 0
}

.experience-slider {
    height: 38px
}

.stage-stops {
    left: 14px;
    right: 14px;
    top: 19px;
    bottom: auto;
    height: 0;
    display: block
}

.stage-stops button {
    position: absolute;
    top: 0;
    padding: 0;
    width: 1px;
    height: 1px;
    color: #704217;
    transform: translateY(-50%)
}

.stage-stops button:before {
    inset: -19px -17px -24px
}

.stage-stops i {
    position: absolute;
    top: -9px;
    width: 18px;
    height: 18px;
    margin: 0;
    border: 3px solid #b57927;
    background: #fac54f;
    border-radius: 50%;
    box-shadow: none
}

.stage-stops button[aria-pressed=true] i {
    background: #ff792d;
    box-shadow: none
}

.stage-stops span {
    position: absolute;
    top: 17px;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    transform: none
}

.stage-stops button:first-child span {
    left: -14px;
    transform: none
}

.stage-stops button:last-child span {
    right: -14px;
    transform: none
}

/* All five portraits share a single size and anchor. No full-panel cover jump. */
.experience-scene,
.experience-scene[data-stage='1'] {
    background: #ffca55;
    border-color: #b68332
}

.photo-stage {
    left: 40%;
    background: none;
    opacity: 1;
    mask-image: linear-gradient(90deg, transparent, #000 18%);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 18%)
}

.stage-photo {
    object-fit: cover;
    object-position: center top
}

.vip-scene {
    object-position: left center;
    object-fit: cover
}

.reading-shade {
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(90deg, #100b05ed 0%, #100b05cf 38%, #100b0560 62%, transparent 83%), linear-gradient(0deg, #100b05d9, transparent 29%)
}

.experience-scene[data-stage='1'] .gold-lettering {
    color: color-mix(in srgb, #78420e, #ffe39b calc(var(--room-reveal)*100%));
    font-weight: 800
}

.experience-scene[data-stage='1'] .benefit-label,
.experience-scene[data-stage='1'] .hours-line>span,
.experience-scene[data-stage='1'] .treatment-copy,
.experience-scene[data-stage='1'] .treatment-copy strong {
    color: color-mix(in srgb, #704417, #fff1cf calc(var(--room-reveal)*100%))
}

.experience-scene[data-stage='1'] .gold-icon {
    color: #ffe2a0;
    background: #8c581d;
    border-color: #ad752d
}

.experience-scene[data-stage='2'] .scene-content {
    background: none
}

.experience-scene[data-stage='2'] .gold-lettering {
    background: none;
    color: #ffe39b;
    -webkit-text-fill-color: currentColor;
    animation: none;
    text-shadow: 0 1px 3px #0009
}

.experience-scene[data-stage='2'] .benefit-label,
.experience-scene[data-stage='2'] .hours-line>span,
.experience-scene[data-stage='2'] .benefit-secondary,
.experience-scene[data-stage='2'] .treatment-copy,
.experience-scene[data-stage='2'] .treatment-copy strong {
    color: #fff3d5;
    text-shadow: 0 1px 3px #000
}

.guaranteed-word,
.benefits .guaranteed-word {
    display: inline-block !important;
    background: #ffda70;
    color: #322009 !important;
    -webkit-text-fill-color: #322009;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 21px;
    font-weight: 900;
    letter-spacing: -.025em;
    line-height: 1.1;
    text-shadow: none;
    box-shadow: 0 2px 10px #0004;
    white-space: nowrap
}

.treatment-copy .guaranteed-word {
    font-size: 17px;
    letter-spacing: 0;
    margin: 0 3px 0 0
}

.previous-copy {
    display: block;
    text-align: center;
    margin-top: 14px;
    font-size: 13px;
    color: #6b431e;
    text-decoration: underline;
    text-underline-offset: 3px
}

/* Current refinement: original warm colours, a quiet bar and continuous layered portraits. */
.experience-bar {
    min-height: 58px;
    padding: 6px 15px;
    gap: 18px;
    background: #fac54f;
    border: 2px solid #b57927;
    border-radius: 29px
}

.experience-bar h2 {
    color: #795020;
    font-size: 17px
}

.rail-area {
    height: 40px
}

.experience-slider {
    height: 40px
}

.stage-stops {
    left: 12px;
    right: 12px;
    top: 20px;
    z-index: 3
}

.stage-stops button {
    width: 1px;
    height: 1px;
    pointer-events: none
}

.stage-stops i {
    top: -3px;
    left: -3px;
    width: 6px;
    height: 6px;
    border: 0;
    background: #a26923;
    pointer-events: none
}

.stage-stops button[aria-pressed=true] i {
    background: transparent
}

.stage-stops button:before {
    inset: -20px -14px
}

.photo-stage {
    left: 40%;
    overflow: visible;
    mask-image: linear-gradient(90deg, transparent, #000 16%);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 16%)
}

.stage-photo {
    inset: auto;
    top: 0;
    left: 50%;
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    transform: translateX(-50%);
    transform-origin: 50% 0
}

.group-rear {
    z-index: 5;
    mask-image: linear-gradient(90deg, transparent, #000 29%, #000 71%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 29%, #000 71%, transparent)
}

.group-left {
    transform: translate(-77%, 5%) scale(.7)
}

.group-right {
    transform: translate(-23%, 5%) scale(.7)
}

.group-front {
    z-index: 6;
    mask-image: linear-gradient(90deg, transparent 9%, #000 34%, #000 67%, transparent 91%);
    -webkit-mask-image: linear-gradient(90deg, transparent 9%, #000 34%, #000 67%, transparent 91%)
}

.stage-photo[data-pose="4"] {
    transform: translate(calc(-50% - 3.4%), -1.387140%) rotate(-4.405006deg) scale(1.14498819)
}

.group-front {
    transform: translate(calc(-50% + 2.654619%), 1.204187%) rotate(6.427460deg) scale(0.89328045)
}

.photo-stage {
    left: 20%;
    right: -20%
}

.group-left {
    transform: translate(-72%, 5%) scale(.7)
}

.group-right {
    transform: translate(-28%, 5%) scale(.7)
}

.benefits .money-back-word {
    font-size: 23px;
    padding: 0;
    box-shadow: none
}

.experience-scene[data-stage='2'] .money-back-word {
    color: #ffe39b;
    -webkit-text-fill-color: currentColor;
    background: none;
    text-shadow: 0 1px 3px #0009
}

.treatment-copy .guaranteed-word {
    display: inline !important;
    padding: 0;
    margin: 0 4px 0 0;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    font-size: 19px;
    font-weight: 900;
    letter-spacing: .015em;
    background: linear-gradient(105deg, #eabb51 12%, #ffe49a 34%, #fff5cd 48%, #eabb51 64%);
    background-size: 250% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: #ffe49a !important;
    -webkit-text-fill-color: transparent;
    text-shadow: none;
    animation: bed-guarantee-shine 2.6s ease-in-out infinite
}

@keyframes bed-guarantee-shine {

    0%,
    100% {
        background-position: 100% center;
        filter: drop-shadow(0 0 1px #facd6355)
    }

    55% {
        background-position: -100% center;
        filter: drop-shadow(0 0 4px #facd6399)
    }
}

/* True alpha cutouts: no rectangular backdrops or feathered faces in the opening group. */
.photo-stage {
    mask-image: none;
    -webkit-mask-image: none;
    transform: translateY(calc(var(--opening)*10%)) scale(calc(1 - var(--opening)*.18));
    transform-origin: 50% 0
}

.portrait-crossfade {
    position: absolute;
    inset: 0;
    isolation: isolate;
    z-index: 2
}

.portrait-crossfade .stage-photo {
    mix-blend-mode: plus-lighter;
    z-index: auto !important
}

.group-rear,
.group-front {
    mask-image: none;
    -webkit-mask-image: none
}

.group-rear {
    z-index: 1;
    mix-blend-mode: normal
}

.stage-photo[data-pose="0"] {
    transform: translate(calc(-50% + -2.923415%), -2.096806%) rotate(-6.735914deg) scale(1.02993681)
}

.stage-photo[data-pose="1"] {
    transform: translate(calc(-50% + -0.208421%), -0.541733%) rotate(-1.138975deg) scale(0.99782748)
}

.stage-photo[data-pose="2"],
.group-front {
    transform: translate(calc(-50% + -0.564948%), -0.462246%) rotate(-0.716593deg) scale(0.95336023)
}

.group-left {
    transform: translate(-76%, 8%) scale(.72)
}

.group-right {
    transform: translate(-24%, 8%) scale(.72)
}

.stage-photo[data-pose="3"],
.stage-photo[data-pose="4"] {
    mask-image: linear-gradient(90deg, transparent, #000 18%);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 18%)
}

/* Let the opening portraits sit lower in the card; fade only their cropped lower edges. */
.photo-stage {
    transform: translateY(calc(var(--opening)*20%)) scale(calc(1 - var(--opening)*.18))
}

.group-rear {
    mask-image: linear-gradient(#000 78%, transparent);
    -webkit-mask-image: linear-gradient(#000 78%, transparent)
}

.group-front {
    mask-image: linear-gradient(#000 91%, transparent);
    -webkit-mask-image: linear-gradient(#000 91%, transparent)
}

/* 800-tier accents and benefits. */
.experience-scene[data-stage='1'] .gold-lettering,
.experience-scene[data-stage='1'] strong {
    color: #7520f7;
    -webkit-text-fill-color: currentColor;
    background: none;
    text-shadow: none
}

.experience-scene[data-stage='1'] .gold-icon {
    color: #fff3d5;
    border-color: #7520f7;
    background: #7520f7;
    box-shadow: none
}

.towel-benefit {
    opacity: var(--reveal);
    pointer-events: none;
    transition: opacity .25s, transform .3s
}

.towel-benefit,
.slipper-benefit {
    transform: translateY(calc(-2 * (var(--row-height) + var(--row-gap)) * (1 - var(--ultimate))))
}

/* An explicit inline size prevents min-height + aspect-ratio from widening the preview. */
.mockup-shell {
    width: 100%;
    min-width: 0;
    container-type: inline-size
}

.experience-scene {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    aspect-ratio: auto
}

.scene-content,
.benefit>div {
    min-width: 0
}

.experience-bar {
    grid-template-columns: auto minmax(0, 1fr);
    min-width: 0
}

@container(max-width:420px) {
    .scene-content {
        width: 74%
    }

    .hours-line {
        flex-wrap: wrap;
        white-space: normal
    }

    .benefit-label {
        overflow-wrap: normal
    }

    .benefits .money-back-word {
        font-size: clamp(16px, 5cqw, 20px)
    }

    .treatment-copy {
        max-width: none;
        right: 12px;
        overflow-wrap: normal
    }
}

@container(max-width:340px) {
    .photo-stage {
        top: auto;
        bottom: 0;
        height: 120cqw
    }

    .scene-content {
        width: 78%;
        padding-left: 9px
    }

    .gold-icon {
        width: 30px;
        height: 30px
    }

    .gold-icon svg {
        width: 20px;
        height: 20px
    }

    .benefit {
        gap: 5px
    }

    .benefits strong {
        font-size: 20px
    }

    .experience-bar {
        gap: 8px;
        padding-inline: 10px
    }

    .experience-bar h2 {
        font-size: 15px
    }
}

/* Compact booking preview, with the opening portrait at the same scale as every other pose. */
.tier-corner {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 20;
    width: clamp(90px, 20cqw, 120px);
    height: clamp(90px, 20cqw, 120px);
    container-type: inline-size;
    clip-path: polygon(0 0, 100% 0, 100% 100%);
    background: #7520f7;
    pointer-events: none;
    animation: corner-reveal .25s ease-out
}

.tier-corner-label {
    position: absolute;
    top: 12%;
    right: -7%;
    width: 74%;
    transform: rotate(45deg);
    text-align: center;
    color: #fff;
    white-space: nowrap;
    z-index: 1
}

.tier-corner-label span {
    display: block;
    font-size: 14cqw;
    font-weight: 500;
    line-height: 1.05;
    letter-spacing: .015em
}

.tier-corner-label b {
    display: block;
    font-size: 30cqw;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -.045em
}

.experience-scene[data-stage='2'] .tier-corner {
    background: linear-gradient(135deg, #a77920, #73500f);
    text-shadow: 0 1px 2px #58390044
}

.experience-scene[data-stage='2'] .tier-corner::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #d5aa43, #a5791b);
    animation: corner-gold-pulse 2.6s ease-in-out infinite
}

@keyframes corner-gold-pulse {

    0%,
    100% {
        opacity: .12
    }

    50% {
        opacity: 1
    }
}

@keyframes corner-reveal {
    from {
        opacity: 0
    }

    to {
        opacity: 1
    }
}

@media(prefers-reduced-motion:reduce) {

    .tier-corner,
    .experience-scene[data-stage='2'] .tier-corner::before {
        animation: none
    }
}

.experience-scene {
    min-height: 0;
    height: auto;
    border-radius: 21px
}

.scene-content {
    width: 66%;
    padding: 12px 0 78px 12px
}

.benefit {
    min-height: 48px;
    gap: 7px
}

.gold-icon {
    width: 32px;
    height: 32px;
    border-width: 1.5px
}

.gold-icon svg {
    width: 21px;
    height: 21px
}

.benefit-label {
    font-size: 14px;
    line-height: 1.2
}

.hours-line {
    gap: 4px
}

.hours-line>strong {
    font-size: 28px
}

.hours-line>span {
    font-size: 13px
}

.benefits {
    --row-height: 43px;
    --row-gap: 3px
}

.benefits strong {
    font-size: 22px;
    line-height: 1.08
}

.benefits small {
    font-size: 15px
}

.benefits .money-back-word {
    font-size: 20px
}

.benefit-secondary {
    font-size: 12px;
    margin-top: 2px
}

.photo-stage {
    top: 0;
    bottom: auto;
    height: 440px;
    transform: none
}

.group-left {
    transform: translate(-72%, 7%) scale(.76)
}

.group-right {
    transform: translate(-28%, 7%) scale(.76)
}

.group-front {
    mask-image: none;
    -webkit-mask-image: none
}

.treatment-copy {
    font-size: 14px;
    line-height: 1.25;
    bottom: 12px;
    left: 13px
}

.treatment-copy strong {
    font-size: 15px;
    margin-top: 3px
}

.treatment-copy .guaranteed-word {
    font-size: 16px
}

.experience-bar {
    min-height: 44px;
    padding: 2px 13px;
    margin-bottom: 10px;
    border-radius: 22px
}

.experience-bar h2 {
    font-size: 16px
}

.rail-area,
.experience-slider {
    height: 36px
}

.stage-stops {
    top: 18px
}

@container(max-width:420px) {
    .scene-content {
        width: 74%;
        padding: 10px 0 75px 10px
    }

    .benefit {
        min-height: 46px;
        gap: 5px
    }

    .gold-icon {
        width: 26px;
        height: 26px
    }

    .gold-icon svg {
        width: 18px;
        height: 18px
    }

    .benefit-label,
    .experience-scene[data-stage='2'] .benefit-label {
        font-size: 12px
    }

    .hours-line>strong,
    .hours-line[data-range=true]>strong {
        font-size: 25px
    }

    .hours-line>span,
    .hours-line[data-range=true]>span {
        font-size: 12px
    }

    .benefits {
        --row-height: 40px;
        --row-gap: 3px
    }

    .benefits strong {
        font-size: 19px
    }

    .benefits small {
        font-size: 13px
    }

    .benefits .money-back-word {
        font-size: 17px
    }

    .benefit-secondary {
        font-size: 12px;
        display: block;
        margin: 2px 0 0
    }

    .treatment-copy {
        font-size: 12px;
        bottom: 10px;
        left: 10px;
        right: 8px
    }

    .treatment-copy strong {
        font-size: 13px
    }

    .treatment-copy .guaranteed-word {
        font-size: 14px
    }

    .photo-stage {
        height: clamp(370px, 115cqw, 440px)
    }

    .experience-bar {
        padding: 2px 11px;
        gap: 10px
    }

    .experience-bar h2 {
        font-size: 15px
    }
}

@container(max-width:340px) {
    .scene-content {
        width: 78%;
        padding-bottom: 79px
    }

    .benefits strong {
        font-size: 18px
    }

    .benefits .money-back-word {
        font-size: 16px
    }

    .gold-icon {
        width: 24px;
        height: 24px
    }

    .gold-icon svg {
        width: 17px;
        height: 17px
    }

    .photo-stage {
        height: 370px
    }
}

/* Premium experience labels use the supplied pixel star artwork. */
.experience-scene[data-stage='1'] .scene-content,
.experience-scene[data-stage='2'] .scene-content {
    padding-bottom: 114px
}

.star-rating {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 27px
}

.star-rating b {
    font-size: 30px;
    line-height: 1;
    font-weight: 800;
    color: #7520f7
}

.star-rating>span {
    display: flex;
    gap: 2px;
    align-items: center
}

.star-rating img {
    display: block;
    width: 21px;
    height: 21px;
    object-fit: contain;
    image-rendering: pixelated
}

.star-experience-label {
    display: block;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.1;
    color: #7520f7
}

.experience-scene[data-stage='2'] .star-rating b,
.experience-scene[data-stage='2'] .star-experience-label {
    color: #ffe39b
}

.experience-notes {
    margin-top: 4px;
    font-size: 10px;
    line-height: 1.25;
    font-weight: 400
}

@container(max-width:420px) {
    .star-rating {
        height: 24px;
        gap: 6px
    }

    .star-rating b {
        font-size: 27px
    }

    .star-rating img {
        width: 18px;
        height: 18px
    }

    .star-experience-label {
        font-size: 12px
    }

    .experience-notes {
        font-size: 9px
    }

    .experience-scene[data-stage='1'] .scene-content,
    .experience-scene[data-stage='2'] .scene-content {
        padding-bottom: 108px
    }
}

/* Keep treatment selection on the left and premium experience on the right. */
.experience-footer {
    position: absolute;
    z-index: 15;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 18px 12px 10px;
    color: #704417;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: end;
    gap: 12px
}

.experience-footer[data-premium=true] {
    background: linear-gradient(transparent, #ffca55e8 45%)
}

.experience-footer .treatment-copy {
    position: static;
    max-width: none;
    min-width: 0
}

.premium-footer {
    text-align: right;
    min-width: 0
}

.premium-footer .star-rating {
    justify-content: flex-end;
    height: 37px
}

.premium-footer .star-rating b {
    font-size: 42px
}

.premium-footer .star-rating img {
    width: 25px;
    height: 25px
}

.premium-footer .star-experience-label {
    font-size: 20px
}

.experience-scene[data-stage='2'] .experience-footer {
    color: #fff3d5;
    background: linear-gradient(transparent, #120b04d9 45%);
    text-shadow: 0 1px 3px #000
}

@container(max-width:420px) {
    .experience-footer {
        padding: 14px 10px 9px;
        gap: 8px
    }

    .experience-footer[data-premium=true] .treatment-copy {
        font-size: 11px;
        line-height: 1.25
    }

    .experience-footer[data-premium=true] .treatment-copy strong {
        font-size: 12px
    }

    .premium-footer .star-rating {
        height: 31px;
        gap: 4px
    }

    .premium-footer .star-rating b {
        font-size: 35px
    }

    .premium-footer .star-rating img {
        width: 18px;
        height: 18px
    }

    .premium-footer .star-rating>span {
        gap: 1px
    }

    .premium-footer .star-experience-label {
        font-size: 17px
    }

    .premium-footer .experience-notes {
        font-size: 9px;
        line-height: 1.2
    }
}

/* Follow the portrait blend continuously, without a second delayed CSS animation. */
.final-benefit,
.towel-benefit,
.slipper-benefit {
    transition: none
}

.experience-scene[data-stage='1'] .gold-lettering,
.experience-scene[data-stage='1'] strong {
    color: color-mix(in srgb, #7520f7, #ffe39b calc(var(--room-reveal)*100%))
}

.experience-scene[data-stage='1'] .gold-icon {
    background: color-mix(in srgb, #7520f7, #20180c calc(var(--room-reveal)*100%));
    border-color: color-mix(in srgb, #7520f7, #d9b462 calc(var(--room-reveal)*100%));
    color: color-mix(in srgb, #fff3d5, #ffe39b calc(var(--room-reveal)*100%))
}

.reading-shade {
    background: linear-gradient(90deg, #100b05ed 0%, #100b05cf 38%, #100b0560 62%, transparent 83%), linear-gradient(0deg, #100b0438, transparent 20%)
}

.experience-footer[data-premium=true],
.experience-scene[data-stage='2'] .experience-footer {
    background: linear-gradient(transparent, color-mix(in srgb, #ffca55e8, #120b0459 calc(var(--room-reveal)*100%)) calc(45% + var(--room-reveal)*40%))
}

.experience-scene[data-stage='1'] .experience-footer {
    color: color-mix(in srgb, #704417, #fff3d5 calc(var(--room-reveal)*100%))
}

/* Compact three-stop pill; the whole bar powers up while dragging. */
.experience-bar {
    min-height: 38px;
    padding: 0 0 0 14px;
    gap: 10px;
    border: 3px solid #b57927;
    border-radius: 24px;
    background: #fac54f;
    transition: background .18s, border-color .18s
}

.experience-bar h2 {
    font-size: 15px;
    position: relative;
    z-index: 1;
    transition: color .18s
}

.rail-area,
.experience-slider {
    height: 32px
}

.rail-area {
    position: relative;
    z-index: 2;
    margin-right: -4px
}

.experience-slider>div {
    height: 32px
}

.stage-stops {
    left: 22px;
    right: 22px;
    top: 16px;
    z-index: 3
}

.stage-stops i {
    width: 8px;
    height: 8px;
    top: -4px;
    left: -4px;
    background: #fff;
    border: 0;
    box-shadow: none
}

.stage-stops button[aria-pressed=true] i {
    background: transparent
}

.experience-bar[data-powered=true] {
    background: #7520f7;
    border-color: #5712c3
}

.experience-bar[data-powered=true] h2 {
    color: #fff
}

.bar-sparks {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    overflow: hidden;
    pointer-events: none;
    opacity: 0;
    transition: opacity .18s
}

.bar-sparks i {
    position: absolute;
    left: var(--spark-x);
    top: var(--spark-y);
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #fff;
    animation: spark-float var(--spark-duration) var(--spark-delay) ease-in-out infinite;
    animation-play-state: paused
}

.bar-sparks i:nth-child(3n) {
    width: 2px;
    height: 2px
}

.experience-bar[data-powered=true] .bar-sparks {
    opacity: 1
}

.experience-bar[data-powered=true] .bar-sparks i {
    animation-play-state: running
}

.experience-bar[data-turbo=true] .bar-sparks i {
    animation-duration: calc(var(--spark-duration) * .35);
    animation-timing-function: linear
}

@keyframes spark-float {
    0% {
        transform: translate(-7px, 12px);
        opacity: 0
    }

    25% {
        opacity: .8
    }

    75% {
        opacity: .6
    }

    100% {
        transform: translate(13px, -17px);
        opacity: 0
    }
}

@container(max-width:340px) {
    .experience-bar {
        padding-left: 10px;
        gap: 5px
    }

    .experience-bar h2 {
        font-size: 14px
    }
}

@media(prefers-reduced-motion:reduce) {
    .bar-sparks {
        display: none
    }
}

/* Make room for Wellness Booster within the compact benefit list. */
.benefit {
    gap: 8px;
    min-height: 43px
}

.gold-icon {
    width: 28px;
    height: 28px
}

.gold-icon svg {
    width: 19px;
    height: 19px
}

.benefit-label,
.experience-scene[data-stage='2'] .benefit-label {
    font-size: 12px;
    line-height: 1.2
}

.benefits {
    --row-height: 34px;
    --row-gap: 2px
}

.benefits strong {
    font-size: 18px;
    line-height: 1.1
}

.benefits small {
    font-size: 13px
}

.benefits .money-back-word {
    font-size: 17px
}

.benefit-secondary {
    font-size: 11px
}

.booster-benefit {
    opacity: var(--reveal);
    pointer-events: none;
    transform: translateY(calc(-2 * (var(--row-height) + var(--row-gap)) * (1 - var(--ultimate))))
}

.booster-focus {
    display: block;
    font-size: 12px;
    white-space: nowrap
}

.booster-focus strong {
    font-weight: 800
}

.experience-scene[data-stage='2'] .booster-focus {
    color: #fff3d5;
    text-shadow: 0 1px 3px #000
}

.experience-footer {
    row-gap: 2px
}

.experience-footer .bed-availability {
    grid-column: 1/-1;
    white-space: nowrap
}

.experience-footer .bed-availability strong {
    margin-top: 0
}

.experience-scene[data-stage='1'] .scene-content,
.experience-scene[data-stage='2'] .scene-content {
    padding-bottom: 134px
}

@container(max-width:420px) {
    .benefit {
        gap: 7px;
        min-height: 41px
    }

    .gold-icon {
        width: 24px;
        height: 24px
    }

    .gold-icon svg {
        width: 16px;
        height: 16px
    }

    .benefit-label,
    .experience-scene[data-stage='2'] .benefit-label {
        font-size: 11px
    }

    .benefits {
        --row-height: 32px;
        --row-gap: 2px
    }

    .benefits strong {
        font-size: 16px
    }

    .benefits .money-back-word {
        font-size: 15px
    }

    .booster-focus {
        font-size: 11px
    }

    .benefit-secondary {
        font-size: 10px
    }

    .experience-footer {
        row-gap: 2px
    }

    .experience-scene[data-stage='1'] .scene-content,
    .experience-scene[data-stage='2'] .scene-content {
        padding-bottom: 132px
    }
}

/* Smaller left-side type, with a dedicated unbroken availability row. */
.scene-content .benefit-label,
.experience-scene[data-stage='2'] .scene-content .benefit-label {
    font-size: 11px;
    line-height: 1.2
}

.scene-content .hours-line>strong {
    font-size: 25px
}

.scene-content .hours-line>span {
    font-size: 11px
}

.scene-content .benefits strong {
    font-size: 16px
}

.scene-content .benefits small {
    font-size: 11px
}

.scene-content .benefits .money-back-word {
    font-size: 15px
}

.scene-content .booster-focus,
.scene-content .benefit-secondary {
    font-size: 10px
}

.experience-footer {
    row-gap: 3px
}

.experience-footer>.treatment-copy:not(.bed-availability) {
    grid-column: 1;
    grid-row: 1;
    font-size: 12px;
    line-height: 1.25
}

.premium-footer {
    display: contents
}

.premium-footer .experience-notes {
    grid-column: 2;
    grid-row: 1;
    align-self: end;
    text-align: right
}

.experience-footer .bed-availability {
    grid-column: 1/-1;
    grid-row: 2
}

.experience-footer .bed-availability strong {
    display: flex;
    flex-wrap: nowrap;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
    font-size: 13px
}

.experience-footer .bed-availability strong>span {
    flex: none;
    white-space: nowrap
}

.experience-footer .bed-availability .guaranteed-word {
    font-size: 14px;
    margin-right: 0
}

.premium-footer .star-experience {
    grid-column: 2;
    grid-row: 3;
    justify-self: end;
    text-align: right
}

.experience-scene[data-stage='0'] .booster-benefit {
    visibility: hidden
}

.experience-scene[data-stage='1'] .scene-content,
.experience-scene[data-stage='2'] .scene-content {
    padding-bottom: 151px
}

@container(max-width:420px) {

    .scene-content .benefit-label,
    .experience-scene[data-stage='2'] .scene-content .benefit-label {
        font-size: 10px
    }

    .scene-content .hours-line>strong,
    .scene-content .hours-line[data-range=true]>strong {
        font-size: 22px
    }

    .scene-content .hours-line>span {
        font-size: 10px
    }

    .scene-content .benefits strong {
        font-size: 14px
    }

    .scene-content .benefits .money-back-word {
        font-size: 13px
    }

    .scene-content .booster-focus,
    .scene-content .benefit-secondary {
        font-size: 10px
    }

    .experience-footer>.treatment-copy:not(.bed-availability) {
        font-size: 10px
    }

    .experience-footer .bed-availability strong {
        font-size: 11px
    }

    .experience-footer .bed-availability .guaranteed-word {
        font-size: 12px
    }

    .experience-scene[data-stage='1'] .scene-content,
    .experience-scene[data-stage='2'] .scene-content {
        padding-bottom: 139px
    }
}

/* Equal icon-centre spacing, including the experience row. */
.scene-content .experience-benefit {
    height: 36px;
    min-height: 36px;
    margin: 0
}

.scene-content .benefits {
    --row-height: 36px;
    --row-gap: 8px
}

.scene-content .benefit {
    gap: 10px
}

.scene-content .hours-line>strong {
    font-size: 22px
}

.experience-footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: end;
    gap: 10px;
    bottom: 0
}

.footer-left {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-self: end
}

.footer-left .treatment-copy {
    font-size: 12px;
    line-height: 1.25
}

.premium-footer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    align-self: end;
    gap: 4px;
    text-align: right
}

.premium-footer .star-experience,
.premium-footer .experience-notes {
    grid-column: auto;
    grid-row: auto;
    align-self: auto;
    justify-self: auto
}

.premium-footer .experience-notes {
    margin-top: 0
}

.experience-footer .bed-availability strong,
.experience-footer .bed-availability .guaranteed-word {
    font-size: clamp(9px, 3cqw, 13px)
}

.experience-footer .bed-availability strong {
    gap: 3px
}

.experience-scene[data-stage='1'] .scene-content,
.experience-scene[data-stage='2'] .scene-content {
    padding-bottom: 118px
}

@container(max-width:420px) {
    .scene-content .experience-benefit {
        height: 34px;
        min-height: 34px
    }

    .scene-content .benefits {
        --row-height: 34px;
        --row-gap: 7px
    }

    .scene-content .benefit {
        gap: 9px
    }

    .scene-content .hours-line>strong,
    .scene-content .hours-line[data-range=true]>strong {
        font-size: 20px
    }

    .experience-footer {
        gap: 8px
    }

    .footer-left .treatment-copy {
        font-size: 10px
    }

    .experience-scene[data-stage='1'] .scene-content,
    .experience-scene[data-stage='2'] .scene-content {
        padding-bottom: 122px
    }
}

.experience-scene[data-stage='1'] .scene-content .booster-focus {
    font-size: 11px;
    line-height: 1.2;
    font-weight: 400;
    color: color-mix(in srgb, #704417, #fff1cf calc(var(--room-reveal)*100%))
}

.experience-scene[data-stage='0'] .experience-notes {
    color: #fff;
    text-shadow: 0 1px 3px #0009
}

@container(max-width:420px) {
    .experience-scene[data-stage='1'] .scene-content .booster-focus {
        font-size: 10px
    }
}

/* The uploaded treatment room stays intact; a matching alpha layer supplies the aura. */
.stage-photo[data-pose='4'],
.stage-photo.room-aura {
    height: 110%;
    transform: translate(-72%, -2.5%);
    mask-image: none;
    -webkit-mask-image: none
}

.aura-filter-defs {
    position: absolute;
    pointer-events: none
}

.stage-photo.room-aura {
    z-index: 3;
    pointer-events: none;
    mix-blend-mode: normal;
    filter: url(#treatment-room-aura);
    animation: therapist-room-glow 2.8s ease-in-out infinite
}

@keyframes therapist-room-glow {

    0%,
    100% {
        filter: url(#treatment-room-aura) brightness(.85)
    }

    50% {
        filter: url(#treatment-room-aura) brightness(1.4)
    }
}

@media(prefers-reduced-motion:reduce) {
    .stage-photo.room-aura {
        animation: none
    }
}

/* V2 supplied chibi artwork: consistent eye line, original faces and proportions. */
.stage-photo[data-pose='0'] {
    transform: translate(-48%, -.27%)
}

.stage-photo[data-pose='1'] {
    transform: translate(-48%, -3.39%)
}

.stage-photo[data-pose='2'],
.stage-photo.group-front {
    transform: translate(-47%, -2.35%)
}

.stage-photo[data-pose='3'] {
    transform: translate(-48%, .26%);
    mask-image: none;
    -webkit-mask-image: none
}

.stage-photo[data-pose='4'],
.stage-photo.room-aura {
    height: 100%;
    transform: translate(-47.5%, 1.17%)
}

.stage-photo[data-pose='4'] {
    mask-image: linear-gradient(90deg, transparent, #000 19%, #000 87%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 19%, #000 87%, transparent)
}

.stage-photo.group-left {
    transform: translate(-77%, 7%) scale(.78)
}

.stage-photo.group-right {
    transform: translate(-19%, 7%) scale(.78)
}

/* Keep composited VIP photos and animated glow within the card on mobile WebKit. */
.experience-scene {
    clip-path: inset(0 round 21px)
}

.vip-scene {
    border-radius: inherit
}

/* V3 keeps the supplied backgrounds; experience colours are text/icon accents. */
.v3-artwork {
    position: absolute;
    inset: 0;
    z-index: -2;
    isolation: isolate;
    pointer-events: none
}

.v3-artwork img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: right top;
    mix-blend-mode: plus-lighter;
    user-select: none
}

.experience-scene {
    background: #f2e7e1 !important;
    border-color: #b57927;
    transition: none
}

.experience-scene .scene-content {
    background: none !important
}

.experience-scene .reading-shade {
    background: linear-gradient(90deg, #7d53fff2 0%, #7d53ffe0 30%, #7d53ff85 49%, transparent 76%)
}

.experience-scene .gold-lettering,
.experience-scene strong {
    color: color-mix(in srgb, #ff6624, #ff6624 calc(var(--palette-shift)*100%)) !important;
    -webkit-text-fill-color: currentColor;
    background: none;
    text-shadow: none;
    animation: none
}

.experience-scene .benefit-label,
.experience-scene .hours-line>span,
.experience-scene .benefit-secondary,
.experience-scene .booster-focus,
.experience-scene .treatment-copy,
.experience-scene .experience-footer,
.experience-scene .experience-notes {
    color: #593b30 !important;
    text-shadow: none
}

.experience-scene .gold-icon {
    background: color-mix(in srgb, #ff6624, #ff6624 calc(var(--palette-shift)*100%)) !important;
    color: #fff !important;
    border: 1px solid transparent;
    box-shadow: none;
    animation: none
}

.experience-scene .experience-footer {
    background: none !important
}

.experience-scene .tier-corner {
    background: #ff6624
}

.experience-scene[data-stage='2'] .gold-lettering,
.experience-scene[data-stage='2'] strong,
.experience-scene[data-stage='2'] .benefit-label,
.experience-scene[data-stage='2'] .hours-line>span,
.experience-scene[data-stage='2'] .benefit-secondary,
.experience-scene[data-stage='2'] .booster-focus,
.experience-scene[data-stage='2'] .treatment-copy,
.experience-scene[data-stage='2'] .experience-footer,
.experience-scene[data-stage='2'] .experience-notes {
    color: #fff !important;
    -webkit-text-fill-color: currentColor;
    background-image: none;
    text-shadow: 0 1px 3px #7b26064d
}

.experience-scene[data-stage='2'] .gold-icon {
    background: #b6ff00 !important;
    color: #242834 !important;
    border-color: #b6ff00;
    box-shadow: 0 0 8px #b6ff0026
}

.experience-scene[data-stage='2'] .guaranteed-word {
    background: none;
    -webkit-text-fill-color: #fff;
    animation: v3-white-shimmer 2.6s ease-in-out infinite
}

@keyframes v3-white-shimmer {

    0%,
    100% {
        text-shadow: 0 0 0 #fff0
    }

    50% {
        text-shadow: 0 0 9px #fff9
    }
}

.experience-scene[data-stage='2'] .tier-corner {
    background: #7d53ff
}

.experience-scene[data-stage='2'] .tier-corner::before {
    background: linear-gradient(135deg, #9d7eff, #5530ba)
}

.experience-bar[data-powered=true] {
    background: #ff6624;
    border-color: #ba4516
}

.experience-bar[data-turbo=true] {
    background: #7d53ff;
    border-color: #5530ba
}

@media(prefers-reduced-motion:reduce) {
    .experience-scene[data-stage='2'] .guaranteed-word {
        animation: none
    }
}

.experience-scene .premium-footer .experience-notes {
    color: #fff !important;
    text-shadow: 0 1px 3px #000a
}

.experience-scene .star-experience,
.experience-scene .star-experience-label,
.experience-scene .star-rating b {
    color: #ff6624 !important
}

.experience-scene[data-stage='2'] .star-experience,
.experience-scene[data-stage='2'] .star-experience-label,
.experience-scene[data-stage='2'] .star-rating b {
    color: #fff !important
}

/* Shared feature emphasis and a legible four-star numeral. */
.experience-scene[data-stage] .scene-content .gold-lettering {
    animation: feature-word-shimmer 3.2s ease-in-out infinite
}

.experience-scene[data-stage='1'] .premium-footer .star-rating b {
    color: #fff !important;
    -webkit-text-fill-color: #fff;
    text-shadow: 0 1px 4px #39200d66
}

@keyframes feature-word-shimmer {

    0%,
    65%,
    100% {
        filter: brightness(1) drop-shadow(0 0 0 transparent)
    }

    80% {
        filter: brightness(1.2) drop-shadow(0 0 3px #ffffff75)
    }
}

@media(prefers-reduced-motion:reduce) {
    .experience-scene[data-stage] .scene-content .gold-lettering {
        animation: none
    }
}

/* Reserve the paid-tier space from the first frame so the artwork never resizes. */
.experience-scene[data-stage] .scene-content {
    padding-bottom: 118px
}

@container(max-width:420px) {
    .experience-scene[data-stage] .scene-content {
        padding-bottom: 122px
    }
}

/* Soft local contrast behind the premium label, with no visible box edge. */
.experience-scene .premium-footer {
    position: relative;
    isolation: isolate
}

.experience-footer[data-premium=true] .premium-footer::before {
    content: '';
    position: absolute;
    inset: -22px -14px -12px -35px;
    z-index: -1;
    pointer-events: none;
    background: radial-gradient(ellipse at 95% 100%, #241209c9 0%, #24120991 45%, #24120930 67%, transparent 83%)
}

.experience-scene[data-stage] .premium-footer .star-experience,
.experience-scene[data-stage] .premium-footer .star-experience-label,
.experience-scene[data-stage] .premium-footer .star-rating b {
    color: #fff !important;
    -webkit-text-fill-color: #fff;
    text-shadow: 0 1px 3px #241209cc, 0 0 8px #24120966
}

.experience-scene .premium-footer .star-rating img {
    filter: drop-shadow(0 1px 2px #241209b3)
}

/* Purple and lime palette for the ultimate stage, retaining the room artwork. */
.experience-scene {
    border-color: color-mix(in srgb, #b57927, #7d53ff calc(var(--room-reveal)*100%))
}

.experience-scene[data-stage='2'] .premium-footer::before {
    background: radial-gradient(ellipse at 95% 100%, #242834d9 0%, #242834a3 45%, #7d53ff30 67%, transparent 83%)
}

.experience-scene[data-stage='2'] .benefit-label,
.experience-scene[data-stage='2'] .gold-lettering,
.experience-scene[data-stage='2'] .treatment-copy {
    text-shadow: 0 1px 3px #24283466
}

/* The room, animated lettering and clean therapist PNG are separate depth layers. */
.v3-artwork .reading-shade {
    z-index: 1;
    background: linear-gradient(90deg, #242834db, #4f269785 33%, transparent 64%)
}

.ultimate-energy {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    overflow: hidden
}

.power-lettering {
    position: absolute;
    left: 24%;
    right: 0;
    top: 18%;
    transform: rotate(-8deg);
    overflow: visible;
    opacity: .75
}

.power-lettering span {
    display: inline-block;
    padding: .6em .75em 1.8em;
    font-family: Horsemen, Impact, sans-serif;
    font-size: clamp(36px, 9.6cqw, 56px);
    line-height: 1;
    letter-spacing: .02em;
    white-space: nowrap;
    color: #b6ff00;
    background: linear-gradient(100deg, #b6ff00 35%, #efffbd 46%, #fff 50%, #efffbd 54%, #b6ff00 65%);
    background-size: 300% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 5px #b6ff0045);
    animation: power-shine 3s ease-in-out infinite
}

@keyframes power-shine {

    0%,
    15% {
        background-position: 150% 50%
    }

    75%,
    100% {
        background-position: -50% 50%
    }
}

.surge-lines {
    position: absolute;
    inset: 0 0 0 42%
}

.surge-lines i {
    position: absolute;
    left: var(--surge-x);
    bottom: -65px;
    width: 2px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(transparent, #b6ff00, #fff);
    box-shadow: 0 0 8px #b6ff0080;
    animation: energy-rise var(--surge-speed) var(--surge-delay) linear infinite
}

.surge-lines i:nth-child(3n) {
    width: 3px;
    height: 90px;
    background: linear-gradient(transparent, #ac83ff, #fff);
    box-shadow: 0 0 14px #7d53ff
}

@keyframes energy-rise {
    0% {
        transform: translateY(0) scaleY(.5);
        opacity: 0
    }

    18% {
        opacity: .85
    }

    75% {
        opacity: .6
    }

    100% {
        transform: translateY(-500px) scaleY(1.3);
        opacity: 0
    }
}

.v3-artwork .ultimate-portrait {
    z-index: 3;
    inset: auto;
    top: 0;
    left: 72%;
    width: auto;
    height: 100%;
    max-width: none;
    object-fit: contain;
    transform: translateX(-46%);
    mix-blend-mode: normal;
    filter: drop-shadow(0 0 4px #b6ff0070) drop-shadow(0 0 14px #7d53ff80)
}

/* A broad, low-opacity fade supports the footer at every checkpoint. */
.experience-scene .experience-footer .premium-footer::before {
    content: '';
    position: absolute;
    inset: -30px -35px -24px -60px;
    z-index: -1;
    pointer-events: none;
    background: radial-gradient(ellipse at 80% 85%, #24283466, #24283429 47%, transparent 76%);
    filter: blur(10px)
}

@media(prefers-reduced-motion:reduce) {

    .power-lettering span,
    .surge-lines i {
        animation: none
    }

    .power-lettering span {
        background: none;
        -webkit-text-fill-color: #b6ff00
    }

    .surge-lines {
        display: none
    }
}

.epic-light {
    display: none
}

.aura-particles i {
    background: #b6ff00;
    box-shadow: 0 0 7px #b6ff0090
}

/* Lime feature words, with a charge-up sweep and one burst on reaching 1000+. */
.experience-scene[data-stage='2'] .scene-content strong,
.experience-scene[data-stage='2'] .scene-content .gold-lettering,
.experience-scene[data-stage='2'] .bed-availability strong,
.experience-scene[data-stage='2'] .bed-availability .guaranteed-word,
.experience-scene[data-stage='2'] .premium-footer .star-rating b,
.experience-scene[data-stage='2'] .premium-footer .star-experience-label {
    color: #b6ff00 !important;
    -webkit-text-fill-color: #b6ff00 !important
}

.upgrade-sweep {
    position: absolute;
    inset: -20% -45%;
    background: linear-gradient(105deg, transparent 42%, #7d53ff33 46%, #b6ff0066 49%, #ffffffa3 50%, #b6ff004d 51%, transparent 57%);
    transform: translateX(calc(-42% + var(--room-reveal)*84%));
    opacity: var(--upgrade-surge);
    mix-blend-mode: screen;
    pointer-events: none
}

.v3-artwork .ultimate-portrait {
    filter: drop-shadow(0 0 calc(4px + var(--upgrade-surge)*8px) #b6ff0090) drop-shadow(0 0 calc(14px + var(--upgrade-surge)*14px) #7d53ff99)
}

.surge-lines i {
    animation-duration: calc(var(--surge-speed) * (1 - var(--upgrade-surge)*.55))
}

.upgrade-burst {
    position: absolute;
    left: 72%;
    top: 43%;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    border: 2px solid #b6ff00;
    box-shadow: 0 0 20px #b6ff00aa, inset 0 0 20px #7d53ff99;
    transform: translate(-50%, -50%);
    animation: upgrade-arrival .9s ease-out both
}

.upgrade-burst::after {
    content: '';
    position: absolute;
    inset: -28px;
    border: 1px solid #e3ffad;
    border-radius: 50%;
    box-shadow: 0 0 12px #b6ff0066
}

@keyframes upgrade-arrival {
    0% {
        transform: translate(-50%, -50%) scale(.35);
        opacity: 0
    }

    15% {
        opacity: .85
    }

    100% {
        transform: translate(-50%, -50%) scale(2.4);
        opacity: 0
    }
}

@media(prefers-reduced-motion:reduce) {

    .upgrade-sweep,
    .upgrade-burst {
        display: none
    }

    .v3-artwork .ultimate-portrait {
        filter: drop-shadow(0 0 4px #b6ff0070)
    }
}

.normal-experience {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    color: #fff;
    font-weight: 800;
    line-height: 1.05;
    text-shadow: 0 1px 3px #241209cc, 0 0 8px #24120966
}

.normal-experience span:first-child {
    font-size: 26px
}

.normal-experience span:last-child {
    font-size: 20px
}

@container(max-width:420px) {
    .normal-experience span:first-child {
        font-size: 23px
    }

    .normal-experience span:last-child {
        font-size: 17px
    }
}

/* Scale portraits with the narrow card width, while the room stays full bleed. */
.mobile-artwork-backdrop {
    display: none
}

@container(max-width:480px) {
    .mobile-artwork-backdrop {
        display: block;
        position: absolute;
        inset: 0;
        z-index: -3;
        background: #ff9b29
    }

    .v3-artwork .standard-artwork {
        top: auto;
        bottom: 0;
        height: min(82%, 82cqw)
    }

    .v3-artwork .ultimate-portrait {
        top: auto;
        bottom: 0;
        height: min(82%, 82cqw);
        left: 76%
    }
}

/* Use the same compact two-line stack for mobile feature labels. */
@container(max-width:480px) {
    .scene-content .benefits .benefit>div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
        line-height: 1.2
    }

    .scene-content .benefits .benefit>div>.benefit-label {
        margin: 0;
        line-height: 1.2
    }

    .scene-content .benefits .benefit>div>strong {
        display: block;
        margin: 0;
        line-height: 1.08
    }
}

/* Bring the pose centres together without changing character scale. */
.mobile-artwork-backdrop {
    display: block;
    position: absolute;
    inset: 0;
    z-index: -3;
    background: #ff9b29
}

.v3-artwork .standard-artwork {
    translate: 12px 0
}

.v3-artwork .ultimate-portrait {
    translate: -12px 0
}

.power-lettering {
    translate: -20px -50px
}

/* A soft orange wash supports the normal-tier caption and small print. */
.experience-scene[data-stage='0'] .experience-footer .premium-footer::before {
    inset: -16px -24px -20px -28px;
    background: radial-gradient(ellipse at 85% 85%, #b8420af2 0%, #c74e12dc 45%, #ef702e80 67%, transparent 88%);
    filter: blur(8px);
}

/* The controls share the display frame, below the artwork and captions. */
.experience-display {
    border: 4px solid color-mix(in srgb, #b57927, #7d53ff calc(var(--room-reveal)*100%));
    border-radius: 21px;
    overflow: hidden;
    isolation: isolate
}

.experience-display .experience-scene {
    border: 0;
    border-radius: 17px 17px 0 0;
    clip-path: inset(0 round 17px 17px 0 0);
    box-shadow: none
}

.experience-display .experience-bar {
    margin: 0;
    border: 0;
    border-radius: 0;
    min-height: 50px;
    padding: 8px 0 8px 12px;
    box-shadow: none
}

.experience-display .rail-area {
    margin-right: 2px
}

.power-lettering {
    translate: -5px -35px
}

.power-lettering span {
    font-size: clamp(27.54px, 7.344cqw, 42.84px)
}

.experience-scene[data-stage='0'] .experience-footer .premium-footer::before {
    inset: auto;
    right: -40px;
    bottom: -62px;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, #bc490edb 0%, #d65b1abf 39%, #ed793855 63%, transparent 75%);
    filter: blur(8px);
}

/* Tier names live in a fixed-width slider heading, keeping drag geometry stable. */
.experience-display .dynamic-experience-title {
    width: 94px;
    display: flex;
    flex-direction: column;
    gap: 1px;
    line-height: 1.1;
    font-size: 12px
}

.bar-tier-name {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 19px;
    font-size: 16px
}

.bar-tier-stars {
    display: flex;
    gap: 1px
}

.bar-tier-stars img {
    width: 12px;
    height: 12px;
    object-fit: contain;
    image-rendering: pixelated
}

.experience-display .experience-scene[data-stage] .scene-content {
    padding-bottom: 68px
}

.experience-display .v3-artwork {
    bottom: -50px
}

@container(max-width:420px) {
    .experience-display .experience-scene[data-stage] .scene-content {
        padding-bottom: 72px
    }

    .experience-display .dynamic-experience-title {
        width: 86px;
        font-size: 11px
    }

    .bar-tier-name {
        font-size: 15px
    }

    .bar-tier-stars img {
        width: 11px;
        height: 11px
    }
}

.experience-scene[data-stage='0'] .experience-footer .premium-footer::before {
    width: 170px;
    height: 170px;
    bottom: -66px;
    right: -30px
}

/* Larger tier markers, a slimmer strip, and an oversized draggable thumb. */
.experience-display {
    overflow: visible
}

.experience-display .experience-bar {
    min-height: 38px;
    padding-top: 0;
    padding-bottom: 0;
    border-radius: 0 0 17px 17px
}

.experience-display .dynamic-experience-title {
    width: 110px;
    font-size: 14.4px
}

.bar-tier-stars img {
    width: 18px;
    height: 18px
}

.power-lettering {
    translate: 25px -35px
}

.experience-display .experience-scene[data-stage] .scene-content {
    padding-bottom: 18px
}

.experience-display .v3-artwork {
    bottom: -100px
}

.swipe-hint {
    position: absolute;
    top: 7px;
    left: 49px;
    display: flex;
    gap: -2px;
    color: #9b5619;
    z-index: 5;
    pointer-events: none;
    animation: swipe-nudge 1.5s ease-in-out infinite
}

.swipe-hint svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.5
}

.swipe-hint svg+svg {
    margin-left: -9px
}

@keyframes swipe-nudge {

    0%,
    100% {
        transform: translateX(0);
        opacity: .45
    }

    55% {
        transform: translateX(9px);
        opacity: 1
    }
}

@container(max-width:420px) {
    .experience-display .dynamic-experience-title {
        width: 104px;
        font-size: 13.2px
    }

    .bar-tier-stars img {
        width: 16.5px;
        height: 16.5px
    }

    .experience-display .experience-scene[data-stage] .scene-content {
        padding-bottom: 22px
    }
}

@media(prefers-reduced-motion:reduce) {
    .swipe-hint {
        animation: none
    }
}

/* Raise the portraits while keeping the room and text anchored. */
.v3-artwork .standard-artwork {
    translate: 52px -30px
}

.v3-artwork .ultimate-portrait {
    translate: 8px -30px
}

.power-lettering {
    translate: 70px -55px
}

/* The terminal thumb covers the frame's outer right and bottom edges. */
.experience-display .rail-area {
    margin-right: -4px
}

@keyframes thumb-invitation {

    0%,
    22%,
    100% {
        transform: translateX(0)
    }

    48% {
        transform: translateX(-7px)
    }

    67% {
        transform: translateX(2px)
    }

    80% {
        transform: translateX(-1px)
    }
}

/* White supporting copy contrasts with the 800-stage orange artwork. */
.experience-scene[data-stage='1'] .benefit-label,
.experience-scene[data-stage='1'] .hours-line>span,
.experience-scene[data-stage='1'] .benefit-secondary,
.experience-scene[data-stage='1'] .booster-focus {
    color: #fff !important;
    text-shadow: 0 1px 2px #70441740
}

/* User-supplied room and transparent portraits stay on separate layers. */
.premium-yellow-shade {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(90deg, #873209 0%, #873209f5 28%, #ad4d14bf 45%, #ee9c3040 65%, transparent 85%);
    pointer-events: none
}

.v3-artwork .premium-portrait {
    z-index: 3;
    inset: auto;
    top: 0;
    left: 72%;
    width: auto;
    height: 100%;
    max-width: none;
    object-fit: contain;
    transform: translateX(-46%);
    translate: 20px -30px;
    mix-blend-mode: normal;
    filter: drop-shadow(0 0 6px #ffdf7070)
}

@container(max-width:480px) {
    .v3-artwork .premium-portrait {
        top: auto;
        bottom: 0;
        height: min(82%, 82cqw);
        left: 73%
    }
}

/* Only VIP room is reserved for the final stage; shared perks move up one row. */
.money-back-benefit,
.booster-benefit,
.towel-benefit,
.slipper-benefit {
    opacity: var(--reveal);
    transform: translateY(calc(-1 * (var(--row-height) + var(--row-gap)) * (1 - var(--ultimate))));
    transition: none
}

/* Shift only the 800 room; the dark reading area fills its exposed left edge. */
.v3-artwork .premium-room {
    translate: 100px 0
}

/* Local glass-panel concept: one quiet surface for the entire benefit list. */
.glass-benefit-panel {
    position: absolute;
    z-index: 0;
    top: 10px;
    bottom: 10px;
    left: 10px;
    width: 49%;
    border-radius: 20px;
    border: 1px solid #ffe7bb88;
    background: linear-gradient(135deg, #a44f1b9c, #56260dbd);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: inset 0 1px 0 #fff5, 0 8px 22px #48200926;
    pointer-events: none
}

.experience-scene .scene-content {
    z-index: 1
}

.premium-yellow-shade {
    background: linear-gradient(90deg, #ad5c24a8, #bf78353d 32%, transparent 68%)
}

.experience-scene[data-stage='2'] .glass-benefit-panel {
    background: linear-gradient(135deg, #6342ab99, #291a55c2);
    border-color: #d7c5ff88
}

.experience-scene[data-stage='0'] .glass-benefit-panel {
    background: linear-gradient(135deg, #bc7126b3, #7e451ecc)
}

.experience-scene .scene-content .benefit-label,
.experience-scene .scene-content .hours-line>span {
    color: #fff !important;
    text-shadow: 0 1px 2px #32140644
}

.experience-scene[data-stage='1'] .scene-content strong,
.experience-scene[data-stage='1'] .scene-content .gold-lettering {
    color: #fff3d2 !important;
    -webkit-text-fill-color: #fff3d2 !important;
    animation: none
}

.experience-scene[data-stage='0'] .scene-content strong {
    color: #fff3d2 !important;
    -webkit-text-fill-color: #fff3d2 !important
}

.experience-scene[data-stage='1'] .gold-icon {
    background: #ed992d !important;
    border: 1px solid #ffe8b2;
    box-shadow: 0 0 8px #ffc46340;
    color: #fff !important
}

.experience-display .experience-bar[data-powered=true]:not([data-turbo=true]) {
    background: linear-gradient(110deg, #be601fe6, #ec9a37eb);
    border-color: #d59746
}

.experience-scene[data-stage='1'] .tier-corner {
    background: #e69a31
}

@container(max-width:420px) {
    .glass-benefit-panel {
        left: 7px;
        top: 7px;
        bottom: 7px;
        width: 49%;
        border-radius: 16px
    }
}

/* Glass sits behind the therapist, keeping her silhouette clear. */
.v3-artwork .glass-benefit-panel {
    z-index: 1;
    bottom: 110px
}

@container(max-width:420px) {
    .v3-artwork .glass-benefit-panel {
        bottom: 107px
    }
}

/* Compact perks; the glass grows by one row for First-Class. */
.experience-scene {
    --compact-row: 34px;
    --compact-gap: 4px;
    --glass-step: 38px
}

.scene-content .benefits {
    --row-height: var(--compact-row);
    --row-gap: var(--compact-gap)
}

.experience-display .experience-scene[data-stage] .scene-content {
    padding-bottom: 48px
}

.v3-artwork .glass-benefit-panel {
    bottom: auto;
    height: calc(48px + (4 + var(--ultimate)) * var(--glass-step))
}

.experience-scene[data-stage='0'] .glass-benefit-panel {
    display: none
}

.experience-scene[data-stage='0'] .scene-content strong {
    color: #ff6624 !important;
    -webkit-text-fill-color: #ff6624 !important
}

.experience-scene[data-stage='0'] .scene-content .benefit-label,
.experience-scene[data-stage='0'] .scene-content .hours-line>span {
    color: #593b30 !important;
    text-shadow: none
}

@container(max-width:420px) {
    .experience-scene {
        --compact-row: 32px;
        --compact-gap: 3px;
        --glass-step: 35px
    }

    .experience-display .experience-scene[data-stage] .scene-content {
        padding-bottom: 52px
    }
}

/* Keep the room full-bleed and add breathing room inside the paid-tier glass. */
.v3-artwork .premium-room {
    translate: none;
    object-position: center top
}

.experience-scene[data-stage='1'] .scene-content,
.experience-scene[data-stage='2'] .scene-content {
    translate: 8px 7px
}

.v3-artwork .glass-benefit-panel {
    height: calc(62px + (4 + var(--ultimate)) * var(--glass-step))
}

.experience-scene[data-stage='1'] .scene-content strong,
.experience-scene[data-stage='1'] .scene-content .gold-lettering {
    color: #ffe39b !important;
    -webkit-text-fill-color: #ffe39b !important;
    text-shadow: 0 1px 2px #43154670
}

.experience-display .experience-bar[data-powered=true]:not([data-turbo=true]) .dynamic-experience-title {
    color: #fff
}

/* Deluxe: vivid violet glass with warm yellow feature highlights. */
.experience-scene[data-stage='1'] .glass-benefit-panel {
    background: rgb(93 0 255 / 76%);
    border-color: #e6d5ffb3
}

/* Reflections stay inside the glass, behind the copy and therapist. */
.v3-artwork .glass-benefit-panel {
    overflow: hidden;
    backdrop-filter: blur(12px) saturate(1.2);
    -webkit-backdrop-filter: blur(12px) saturate(1.2);
    box-shadow: inset 0 1px 0 #ffffffb3, inset 1px 0 0 #ffffff40, inset 0 -1px 0 #ffffff40, 0 8px 22px #29104733
}

.glass-benefit-panel::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(135deg, #ffffff30, transparent 29%, transparent 72%, #ffffff12);
    pointer-events: none
}

.glass-benefit-panel::after {
    content: '';
    position: absolute;
    inset: -40% -65%;
    background: linear-gradient(112deg, transparent 42%, #ffffff08 46%, #ffffff52 49%, #ffffff1f 51%, transparent 56%);
    transform: translateX(-65%);
    animation: glass-reflection 4.8s ease-in-out infinite;
    pointer-events: none;
    will-change: transform, opacity
}

@keyframes glass-reflection {

    0%,
    12% {
        transform: translateX(-65%);
        opacity: 0
    }

    20% {
        opacity: 1
    }

    52% {
        transform: translateX(65%);
        opacity: 1
    }

    62%,
    100% {
        transform: translateX(65%);
        opacity: 0
    }
}

@media(prefers-reduced-motion:reduce) {
    .glass-benefit-panel::after {
        animation: none;
        transform: translateX(0);
        opacity: .3;
        will-change: auto
    }
}

/* Tier lettering uses the user's installed Designer and POWER/Horsemen fonts. */
.bar-tier-name[data-tier='1']>span {
    font-family: Designer, Arial, sans-serif;
    font-size: 19.38px;
    font-weight: 400;
    line-height: 1;
    translate: 0 3px
}

.bar-tier-name[data-tier='2']>span {
    font-family: Horsemen, Arial, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1;
    translate: 0 -2px
}

/* Settled-tier copy occupies the track to the left of the draggable thumb. */
.tier-tagline {
    position: absolute;
    z-index: 5;
    left: 0;
    top: 50%;
    translate: 0 -50%;
    width: calc((100% - 48px) * var(--tagline-progress) - 10px);
    font-family: TaglineItalic, Arial, sans-serif;
    font-style: italic;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.2;
    text-align: right;
    color: white;
    pointer-events: none;
    animation: tagline-appear .3s ease-out both
}

.experience-bar[data-tier='1'] .stage-stops button[data-checkpoint='0'] i,
.experience-bar[data-tier='2'] .stage-stops button[data-checkpoint='0'] i {
    opacity: 0
}

@keyframes tagline-appear {
    from {
        opacity: 0
    }

    to {
        opacity: 1
    }
}

@container(max-width:420px) {
    .tier-tagline {
        font-size: 10px;
        line-height: 1.15
    }
}

@media(prefers-reduced-motion:reduce) {
    .tier-tagline {
        animation: none
    }
}

/* Completed checkpoints disappear behind the selected tier. */
.experience-bar[data-tier='2'] .stage-stops button[data-checkpoint='78'] i {
    opacity: 0
}

/* Allow Horsemen's brush strokes to paint above the strip, including on mobile. */
.experience-display .experience-bar {
    overflow: visible;
    z-index: 30
}

.dynamic-experience-title,
.bar-tier-name {
    overflow: visible
}

.bar-tier-name[data-tier='2']>span {
    display: inline-block;
    white-space: nowrap;
    padding: .5em .4em .7em;
    margin: -.5em -.4em -.7em;
    overflow: visible
}

/* V4: slim reflective promo inside the original V3 frame dimensions. */
.bank-promo {
    position: absolute;
    z-index: 8;
    left: 10px;
    right: 10px;
    bottom: 3px;
    height: 24px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    overflow: hidden;
    isolation: isolate;
    border: 1px solid #ffb3b77a;
    border-radius: 6px;
    background: linear-gradient(125deg, #ef3643d9, #bf1020e8);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    color: #fff;
    box-shadow: inset 0 1px 0 #ffffff70, inset 0 -1px 0 #8d001f55;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .2s ease, visibility .2s
}

.bank-promo[data-visible=true] {
    opacity: 1;
    visibility: visible
}

.bank-promo::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(180deg, #ffffff30, transparent 55%);
    pointer-events: none
}

.bank-promo::after {
    content: '';
    position: absolute;
    inset: -80% -65%;
    z-index: -1;
    background: linear-gradient(110deg, transparent 44%, #ffffff08 47%, #ffffff94 50%, #ffffff30 53%, transparent 57%);
    transform: translateX(-65%);
    animation: bank-glass-shimmer 3.2s ease-in-out infinite;
    pointer-events: none
}

.bank-promo img {
    display: block;
    width: 100px;
    max-width: 100%;
    height: auto;
    max-height: 18px;
    object-fit: contain
}

.bank-promo span {
    font: 700 11px/1.1 Arial, Helvetica, sans-serif;
    white-space: nowrap;
    letter-spacing: .02em
}

@keyframes bank-glass-shimmer {

    0%,
    12% {
        transform: translateX(-65%);
        opacity: 0
    }

    22% {
        opacity: 1
    }

    65% {
        transform: translateX(65%);
        opacity: 1
    }

    78%,
    100% {
        transform: translateX(65%);
        opacity: 0
    }
}

@container(max-width:420px) {
    .bank-promo {
        left: 7px;
        right: 7px;
        height: 20px;
        padding: 0 8px;
        gap: 10px
    }

    .bank-promo img {
        width: 90px;
        max-height: 16px
    }

    .bank-promo span {
        font-size: 10px
    }
}

@media(prefers-reduced-motion:reduce) {
    .bank-promo {
        transition: none
    }

    .bank-promo::after {
        animation: none;
        opacity: .25;
        transform: none
    }
}

.stage-stops button[data-checkpoint="0"] {
    transform: translateX(-25px)
}

@keyframes thumb-invitation {

    0%,
    22%,
    100% {
        transform: translateX(var(--standard-offset, 0px))
    }

    48% {
        transform: translateX(calc(var(--standard-offset, 0px) - 7px))
    }

    67% {
        transform: translateX(calc(var(--standard-offset, 0px) + 2px))
    }

    80% {
        transform: translateX(calc(var(--standard-offset, 0px) - 1px))
    }
}

.swipe-hint {
    top: 0;
    bottom: 0;
    left: 24px;
    right: 24px;
    align-items: center;
    gap: 10px;
    animation: none
}

.swipe-arrows {
    display: flex;
    flex-shrink: 0;
    animation: swipe-nudge 1.5s ease-in-out infinite
}

.swipe-copy {
    font: 600 11px/1.15 Arial, Helvetica, sans-serif;
    color: #754117;
    max-width: 180px
}

@container(max-width:420px) {
    .swipe-copy {
        font-size: 10px;
        max-width: 135px
    }
}

@media(prefers-reduced-motion:reduce) {
    .swipe-arrows {
        animation: none
    }
}
</style>